<template>
  <div id="tag-editor-container">
    <div class="search-create-tags">
      <DxTextBox placeholder="Search/Create Tags..."
                 v-model:value="tagSearchText"
                 :disabled="!isCreatePermitted"
                 :show-clear-button="true"
                 @input="searchTags"
                 @enter-key="createNewTag(tagSearchText)"
      />
      <DxButton icon="add" :disabled="!tagSearchText" @click="createNewTag(tagSearchText)"/>
    </div>
    <DxDataGrid id="tag-editor"
                :data-source="filteredTagEditorItems"
                :disabled="!isCreatePermitted"
                :show-column-headers="false"
                :show-column-lines="false"
                :show-row-lines="false"
                :row-alternation-enabled="true"
    >
      <DxColumn cell-template="cell-template"/>
      <template #cell-template="{ data: tag }">
        <TagRowTemplate :tag-id="tag.data.tag_id"
                        :is-checked="getCheckboxState(tag.data)"
                        :tag-name="tag.data.name"
                        :tag-color="tag.data.color"
                        :tag-count="newTagCount(tag.data)"
                        :tag-show-count="true"
                        :tag-modified="isTagModified(tag.data.name)"
                        @checkbox-toggle="editTagSelection($event, tag.data)"
        />
      </template>
    </DxDataGrid>

    <div class="submit-button-container" v-if="!autosave">
      <DxButton id="submit-button"
                :text="submitButtonText || 'Update Entire Basket'"
                :disabled="noChangesApplied"
                type="action"
                width="100%"
                @click="submitTagChanges"
      />
      <DxButton icon="remove" :disabled="noChangesApplied" @click="clearTagChanges"/>
    </div>
  </div>

  <DxTooltip target="#submit-button"
             show-event="mouseenter"
             hide-event="mouseleave"
             position="left"
  >
    <div class="pending-changes-list" style="text-align: left;">
      <div>
        <span> Additions: </span>
        <ul>
          <li v-for="tagData in tagsToAdd">
            {{ tagData.name }}
          </li>
        </ul>
      </div>

      <div>
        <span> Removals: </span>
        <ul>
          <li v-for="tagName in tagsToRemove">
            {{ tagName }}
          </li>
        </ul>
      </div>
    </div>
  </DxTooltip>
</template>

<script setup>
  import TagRowTemplate from "@/components/search-page/search-tool-drawer/TagRowTemplate.vue";
  import { DxTooltip } from "devextreme-vue/tooltip";
  import { DxButton } from "devextreme-vue";
  import { DxTextBox } from "devextreme-vue/text-box";
  import { DxDataGrid, DxColumn } from "devextreme-vue/data-grid";
  import { ref, computed, watch } from "vue";
  import { randomColor } from "@/js-modules/colorPalettes.js";

  const props = defineProps(['modeParams', 'onSubmitTagEdits', 'tagEditorItems', 'affectedCount', 'submitButtonText', 'autosave'])

  const isCreatePermitted = true
  const noChangesApplied = computed(() => tagsToRemove.value.length === 0 && tagsToAdd.value.length === 0)

  const tagsToRemove = ref([])
  const tagsToAdd = ref([])

  const combinedTagEditorItems = computed(() => {
    const tagEditorItemsCopy = JSON.parse(JSON.stringify(props.tagEditorItems))
    const existingTagNames = tagEditorItemsCopy.map(tag => tag.name)
    const newTags = tagsToAdd.value.filter(tag => !existingTagNames.includes(tag.name))
    return tagEditorItemsCopy.concat(newTags).sort((a,b) => a.name.localeCompare(b.name))
  })

  const tagSearchText = ref('')
  const searchTags = (search) => tagSearchText.value = search.event.currentTarget.value

  const filteredTagEditorItems = computed(() => {
    return combinedTagEditorItems.value.filter(tag =>
      tag.name.toLowerCase().includes(tagSearchText.value.toLowerCase())
    )
  })

  const createNewTag = (newTagName) => {
    if (!tagsToAdd.value.includes(newTagName)) {
      const existingTag = props.tagEditorItems.find(tag => tag.name === newTagName)
      const newTagTemplate = {
          "name": newTagName,
          "color": randomColor('vibrant'),
          "count": 0,
          "basket_tagged": true
        }
      editTagSelection({ isChecked: true }, existingTag ?? newTagTemplate)
    }
    tagSearchText.value = ''
  }

  const getCheckboxState = (tagData) => {
    if (tagsToAdd.value.find(tag => tag.name === tagData.name))
      return true
    if (tagsToRemove.value.includes(tagData.name))
      return false
    return tagData.basket_tagged
  }

  const newTagCount = (tagData) => {
    if (tagsToAdd.value.find(tag => tag.name === tagData.name))
      return props.affectedCount
    if (tagsToRemove.value.includes(tagData.name))
      return 0
    return tagData.count
  }

  const isTagModified = (tagName) => !!(tagsToAdd.value.find(tag => tag.name === tagName) || tagsToRemove.value.includes(tagName))
  const isOriginalState = (tagName, newState) => newState === props.tagEditorItems.find(tag => tag.name === tagName)?.basket_tagged

  const editTagSelection = ({ isChecked }, tagData) => {
    if (isChecked) {
      const foundTag = tagsToAdd.value.find(tag => tag.name === tagData.name)
      if (foundTag === undefined && !isOriginalState(tagData.name, isChecked))
        tagsToAdd.value.push(tagData)
      tagsToRemove.value = tagsToRemove.value.filter(tag => tag !== tagData.name)
    }
    else {
      if (!tagsToRemove.value.includes(tagData.name) && !isOriginalState(tagData.name, isChecked))
        tagsToRemove.value.push(tagData.name)
      tagsToAdd.value = tagsToAdd.value.filter(tag => tag.name !== tagData.name)
    }
  }

  // Watch for changes in tags and auto-submit if autosave is enabled
  watch([tagsToAdd, tagsToRemove], () => {
    if (props.autosave && !noChangesApplied.value) {
      submitTagChanges()
    }
  }, { deep: true })

  const submitTagChanges = () => {
    props.onSubmitTagEdits(props.modeParams, tagsToAdd.value, tagsToRemove.value)
    clearTagChanges()
  }

  const clearTagChanges = () => {
    tagsToAdd.value = []
    tagsToRemove.value = []
    tagSearchText.value = ''
  }
</script>

<style scoped lang="scss">
  #tag-editor-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  #tag-editor {
    height: 350px;
  }

  .search-create-tags {
    display: flex;
    gap: 0.5rem;
  }

  .submit-button-container {
    display: flex;
    gap: 0.5rem;
  }

  .pending-changes-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    span {
      font-weight: bold;
    }
  }
</style>
